import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { API, setAuthToken, setCurrentUser, setGymData } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Smartphone,
  CreditCard,
  BarChart2,
  ArrowRight,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Dumbbell,
  Info,
  Zap,
  Users,
  Loader2,
} from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { setUser, setGym } = useAuth();

  const handleLogin = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/gym-login`,
        { email, password },
        { timeout: 90000 }
      );
      const { token } = data;
      setAuthToken(token);

      if (data.user?.role === 'superadmin') {
        const u = data.user;
        setCurrentUser(u);
        setUser(u);
        toast.success('Welcome, Super Admin!');
        navigate('/superadmin/dashboard');
      } else if (data.user?.role === 'gym_owner' || data.gym) {
        const u = data.user || {};
        const gymData = data.gym || data.user || {};
        setGymData(gymData);
        setCurrentUser({
          role: 'gym_owner',
          email: u.email || gymData.email,
          gym_id: u.gym_id || gymData.id,
        });
        setGym(gymData);
        setUser({
          role: 'gym_owner',
          email: u.email || gymData.email,
          gym_id: u.gym_id || gymData.id,
        });
        toast.success(`Welcome back, ${gymData.gym_name || 'Gym Owner'}!`);
        navigate('/admin/dashboard');
      } else {
        toast.error('Member accounts must sign in through the mobile app.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:h-screen flex flex-col lg:flex-row font-sans bg-[#04080a] lg:overflow-hidden">
      {/* LEFT SECTION */}
      <div className="lg:w-[46%] bg-[#04080a] relative flex flex-col px-6 py-10 lg:p-12 lg:h-full lg:overflow-hidden hide-scrollbar">
        <div className="absolute w-[400px] h-[400px] rounded-full bg-[radial-gradient(circle,rgba(16,185,129,0.08)_0%,transparent_65%)] -top-[150px] -left-[100px] pointer-events-none" />
        <div className="absolute w-[250px] h-[250px] rounded-full bg-[radial-gradient(circle,rgba(16,185,129,0.05)_0%,transparent_65%)] -bottom-[80px] -right-[60px] pointer-events-none" />

        <div className="flex items-center gap-2.5 mb-8 lg:mb-10 relative z-10 flex-shrink-0">
          <div className="w-8 h-8 lg:w-9 lg:h-9 bg-emerald-500 rounded-xl flex items-center justify-center">
            <Dumbbell size={18} className="text-white" />
          </div>
          <span className="text-lg lg:text-xl font-bold text-white tracking-tight">
            Fit<em className="not-italic text-emerald-500">xero</em>.
          </span>
        </div>

        <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1.5 mb-6 w-fit relative z-10 flex-shrink-0">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[10px] lg:text-xs text-emerald-500 font-bold tracking-wide">
            14,302 ACTIVE SESSIONS GLOBALLY RIGHT NOW
          </span>
        </div>

        <div className="relative z-10 mb-8 flex-shrink-0">
          <h1 className="text-3xl lg:text-[2.5rem] font-extrabold text-white leading-[1.15] tracking-tight mb-3">
            Command your gym.
            <br />
            <em className="not-italic text-emerald-500">Total operational control.</em>
          </h1>
          <p className="text-xs lg:text-sm text-emerald-900/60 lg:text-emerald-50/60 leading-relaxed max-w-sm font-medium">
            Log in to oversee memberships, process payments, and track your daily financial growth
            in real-time.
          </p>
        </div>

        <div className="flex-1 flex overflow-x-auto lg:flex-col lg:overflow-y-auto snap-x snap-mandatory hide-scrollbar gap-4 lg:gap-3 relative z-10 -mx-6 px-6 lg:mx-0 lg:px-0 pb-4 lg:pb-0">
          {[
            {
              icon: Users,
              label: 'MEMBER MANAGEMENT',
              title: 'Total visibility over your community.',
              subtitle: 'Manage plans, track attendance, & boost engagement instantly.',
            },
            {
              icon: Zap,
              label: 'FINANCIAL PEACE OF MIND',
              title: 'Every payment tracked, zero manual effort.',
              subtitle: 'Automated billing ensures your cash flow stays consistent.',
            },
            {
              icon: BarChart2,
              label: 'LIVE PERFORMANCE METRICS',
              title: 'Know your exact growth rate at a glance.',
              subtitle: 'Actionable data on revenue, retention, and peak hours.',
            },
          ].map((f, i) => (
            <div
              key={i}
              className="snap-center shrink-0 w-[85%] lg:w-full flex items-start gap-3.5 bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors rounded-2xl p-4 lg:p-5"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-500/10 flex-shrink-0 shadow-[inset_0_0_12px_rgba(16,185,129,0.1)]">
                <f.icon size={18} className="text-emerald-500" />
              </div>
              <div className="pr-2">
                <div className="text-[10px] font-bold text-emerald-500 tracking-wider mb-1.5 uppercase">
                  {f.label}
                </div>
                <div className="text-[13px] sm:text-[14px] font-semibold text-gray-200 leading-snug mb-2">
                  {f.title}
                </div>
                <div className="text-[11px] sm:text-[12px] font-medium text-gray-400 flex items-center gap-1.5">
                  <ArrowRight size={12} className="text-emerald-500" /> {f.subtitle}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3 pt-6 mt-auto border-t border-white/5 relative z-10 flex-shrink-0">
          <div className="flex -space-x-2">
            {['JM', 'SR', 'TC', 'AL', '+'].map((av, i) => (
              <div
                key={i}
                className="w-7 h-7 rounded-full border-2 border-[#04080a] flex items-center justify-center text-[9px] font-bold"
                style={{
                  background: ['#1d4ed8', '#7c3aed', '#065f46', '#92400e', '#1a1a2e'][i],
                  color: ['#bfdbfe', '#ddd6fe', '#a7f3d0', '#fde68a', '#818cf8'][i],
                }}
              >
                {av}
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-400">
            <strong className="text-emerald-500/80 font-semibold">Join 8,000+ owners</strong>{' '}
            scaling their gyms.
          </div>
        </div>
      </div>

      {/* RIGHT SECTION */}
      <div className="flex-1 bg-[#0b1015] lg:rounded-l-3xl flex flex-col px-6 py-10 lg:px-20 relative lg:-ml-6 z-20 lg:shadow-[-20px_0_50px_rgba(0,0,0,0.5)] lg:h-full lg:overflow-y-auto">
        <div className="max-w-[400px] w-full mx-auto py-10 lg:my-auto">
          <div className="hidden lg:flex items-center gap-2 mb-8">
            <div className="w-7 h-7 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Dumbbell size={14} className="text-white" />
            </div>
            <span className="text-[15px] font-bold text-white tracking-tight">
              Fit<em className="not-italic text-emerald-500">xero</em>.
            </span>
          </div>

          <h2 className="text-[26px] lg:text-3xl font-extrabold text-white mb-2 tracking-tight">
            Welcome back
          </h2>
          <p className="text-sm text-gray-400 mb-8 font-medium">
            Sign in to your owner dashboard to continue.
          </p>

          <form onSubmit={handleLogin} className="flex flex-col gap-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-[13px] font-medium p-3 rounded-xl flex items-start gap-2">
                <Info size={16} className="mt-0.5 flex-shrink-0" />
                {error}
              </div>
            )}

            <div>
              <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wide mb-1.5">
                Work email
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="owner@gym.com"
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl py-3 pl-10 pr-4 text-base sm:text-sm font-medium text-white focus:bg-white/[0.08] focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 shadow-sm outline-none transition-all placeholder:text-gray-500"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wide">
                  Password
                </label>
              </div>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  type={showPw ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  placeholder="Your password"
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl py-3 pl-10 pr-10 text-base sm:text-sm font-medium text-white focus:bg-white/[0.08] focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 shadow-sm outline-none transition-all placeholder:text-gray-500"
                />
                <button
                  type="button"
                  aria-label="Toggle password visibility"
                  aria-expanded={showPw}
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white p-2 -mr-2"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <fieldset disabled={loading} className="group mt-2">
              <button
                type="submit"
                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500/50 disabled:cursor-not-allowed text-white rounded-xl py-3.5 text-[14px] font-bold flex items-center justify-center gap-2 transition-all shadow-[0_4px_14px_rgba(16,185,129,0.3)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.4)] hover:-translate-y-0.5 group-disabled:hover:translate-y-0 group-disabled:hover:shadow-none"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Logging in...
                  </>
                ) : (
                  <>
                    Login to dashboard <ArrowRight size={16} />
                  </>
                )}
              </button>
            </fieldset>
          </form>

          <div className="mt-6 bg-white/[0.02] border border-white/5 rounded-xl p-3.5 flex items-start gap-3">
            <Info size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
            <p className="text-[11.5px] text-gray-400 leading-relaxed font-medium">
              This portal is for{' '}
              <strong className="text-gray-300 font-bold">gym owners & staff only.</strong> Members
              access their dashboard via the Member App.
            </p>
          </div>

          <p className="text-center text-sm text-gray-400 font-medium mt-8">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="text-emerald-500 font-bold hover:text-emerald-400 transition-colors"
            >
              Start free trial
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
